import { useState } from 'react';
import { CreationStrategy } from '../../types/creation';
import { StrategyType } from '../../constants/strategy';
import { KovrificationConfig } from '../strategies/KovrificationConfig';
import { BestMomentsConfig } from '../strategies/BestMomentsConfig';
import { useTranslation } from '../../i18n';

interface ConfigurationPanelProps {
  config: CreationStrategy;
  strategyType: StrategyType;
}

export function ConfigurationPanel({ config, strategyType }: ConfigurationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  // Render strategy-specific configuration based on strategy type
  const renderStrategyConfig = () => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return <KovrificationConfig config={config} />;
      case StrategyType.BEST_MOMENTS:
        // Convert config to format expected by BestMomentsConfig
        return <BestMomentsConfig config={{
          captions_enabled: config.captions_enabled,
          caption_config: config.caption_config || {
            color: '#FFFFFF',
            accent_color: '#7FFF00',
            vertical_margin: 0.7,
            style_id: 'karaoke_default_v1'
          }
        }} />;
      // Add more cases here as more strategies are added
      default:
        return null;
    }
  };

  // Get the strategy-specific title
  const getStrategyTitle = () => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return t('creationDetails.configuration.crollTitle');
      case StrategyType.BEST_MOMENTS:
        return t('creationDetails.configuration.bestMomentsTitle');
      default:
        return t('creationDetails.configuration.title');
    }
  };

  // Get strategy icon
  const getStrategyIcon = () => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return "videocam";
      case StrategyType.BEST_MOMENTS:
        return "auto_awesome";
      default:
        return "settings";
    }
  };

  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl overflow-hidden shadow-interactive">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-surface-interactive-hover dark:hover:bg-surface-interactive-dark-hover transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary-accent bg-primary-accent-alpha-10 p-2 rounded-full">
            {getStrategyIcon()}
          </span>
          <span className="font-medium text-content-strong text-lg">
            {getStrategyTitle()}
          </span>
        </div>
        <span className={`material-symbols-outlined text-theme-text transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
          expand_more
        </span>
      </button>

      {isExpanded && (
        <div className="p-6 space-y-6 border-t border-interactive dark:border-interactive-dark bg-neutral-soft-alpha dark:bg-neutral-soft-dark-alpha">
          {renderStrategyConfig()}
        </div>
      )}
    </div>
  );
} 